/* eslint-disable sonarjs/no-duplicate-string */
import Vue from 'vue'
import Router from 'vue-router'
import * as Sentry from '@sentry/vue'
import { applySeoForRoute } from '@/utils/seo.utils'
import Config from '@/config'
import store from '@/vuex/store'
import {
  NO_CHAT_ROUTES,
  PUBLIC_ROUTES,
  OPEN_ROUTES_FOR_GUEST,
  ROUTE_NAMES,
} from '@/constant/routes'
import { AHA_GUEST_USER } from '@/constant/storageKeys'
import textUtils from '@/utils/textUtil'
// services
import cookie from '@/services/cookie'
import zoho from '@/services/zoho'
import util from '@/util'
import mixpanelUtils from './utils/mixpanel.utils'
import { registerPrefetchHandler } from '@/vuex/plugins/route-prefetch.plugin'
import { getRoutesToPrefetch } from '@/config/prefetch.config'
import { prefetchMultipleComponents } from '@/utils/prefetch.utils'
import { getLocalVariant, LOCAL_VARIANTS } from '@/utils/abtest.utils'

Vue.use(Router)
let defaultUrl = ROUTE_NAMES.HOME
if (Config.isEnableAddinPowerpoint) {
  defaultUrl = ROUTE_NAMES.PRESENTATIONS
}

const configRouter = {
  mode: 'history',
  linkExactActiveClass: 'selected',
  linkActiveClass: 'selected',
  routes: [
    {
      path: '/',
      redirect: { name: defaultUrl },
    },
    {
      path: '/inquiries-form',
      name: ROUTE_NAMES.INQUIRIES_FORM,
      component: () => import('./components-v2/my-plan/user-pricing-v3/EnterpriseInquiriesForm.vue'),
    },
    {
      path: '/shared-report/:id/:audienceId?',
      name: ROUTE_NAMES.SHARED_REPORT,
      meta: { title: 'Shared Report' },
      component: () => import('./components-v2/presentation-report/ReportPagePublicWrapper.vue'),
    },
    {
      path: '/ms-teams',
      component: () => import('./components-v2/ms-teams/MSTeamsRoot.vue'),
      children: [
        {
          path: 'config',
          name: ROUTE_NAMES.MSTEAMS_TABCONFIG,
          component: () => import('./components-v2/ms-teams/MSTeamsTabConfig.vue'),
        },
        {
          path: 'presenting/:presentationId',
          name: ROUTE_NAMES.MSTEAMS_PRESENTING,
          component: () => import('./components-v2/ms-teams/MSTeamsPresenting.vue'),
        },
      ],
    },
    {
      path: '/apps/zoom',
      name: ROUTE_NAMES.ZOOM_APP,
      meta: { title: 'AhaSlides Zoom App' },
      component: () => import('./components-v2/zoom/ZoomMainApp.vue'),
    },
    {
      path: '/apps/zoom/presentations',
      name: ROUTE_NAMES.ZOOM_PRESENTATION_LIST,
      meta: { title: 'AhaSlides Zoom App' },
      component: () => import('./components-v2/zoom/ZoomPresentationList.vue'),
    },
    {
      path: '/apps/zoom/audience',
      name: ROUTE_NAMES.ZOOM_AUDIENCE_APP,
      meta: { title: 'Audience Zoom App' },
      component: () => import('./components-v2/zoom/ZoomEmbedAudienceApp.vue'),
    },
    {
      path: '/apps',
      component: () => import('./components-v2/home/Home.vue'),
      children: [
        {
          path: 'home',
          name: ROUTE_NAMES.HOME,
          meta: { title: 'Home Page' },
          component: () => import('./components-v2/home/HomePageRouter.vue'),
        },
        {
          path: 'trash-bin',
          name: ROUTE_NAMES.TRASH_BIN,
          meta: { title: 'Trash' },
          component: () => import('./components-v2/trash/Trash'),
        },
        {
          path: 'presentations',
          name: ROUTE_NAMES.PRESENTATIONS,
          meta: { title: 'My Presentations' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'shared-presentations',
          name: ROUTE_NAMES.SHARED_WITH_ME,
          meta: { title: 'Shared With Me' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'presentations/folders/:folderId',
          name: ROUTE_NAMES.FOLDER_DETAIL,
          meta: { title: 'My Presentations' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'shared-presentations/folders/:folderId',
          name: ROUTE_NAMES.SHARE_FOLDER_DETAIL,
          meta: { title: 'Shared With Me' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'team/workspace/folders/:folderId',
          name: ROUTE_NAMES.TEAM_FOLDER_DETAIL,
          meta: { title: 'Team Presentations' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'template-library',
          name: ROUTE_NAMES.TEMPLATE_LIBRARY,
          meta: { title: 'Templates' },
          component: () => import('./components-v2/template-library/TemplateLibrary.toggle.vue'),
        },
        {
          path: 'team/workspace',
          name: ROUTE_NAMES.TEAM,
          meta: { title: 'My Team' },
          component: () => import('./components-v2/my-presentations/MyPresentations.vue'),
        },
        {
          path: 'team/members',
          name: ROUTE_NAMES.TEAM_MEMBER,
          meta: { title: 'My Team' },
          component: () => import('./components-v2/my-team/Team.vue'),
        },
        {
          path: 'user-profile',
          name: ROUTE_NAMES.USER_PROFILE,
          meta: { title: 'My Profile' },
          component: () => import('./components-v2/my-profile/MyProfile.vue'),
        },
        {
          path: 'billing-and-payments',
          name: ROUTE_NAMES.BILLING_LIST,
          meta: { title: 'Payment' },
          component: () => import('./components-v2/billing/BillingList.vue'),
        },
        {
          path: 'billing-and-payments/:id',
          name: ROUTE_NAMES.BILLING_DETAIL,
          meta: { title: 'Billing detail' },
          component: () => import('./components-v2/billing/BillingDetail.vue'),
        },
        {
          path: 'community',
          name: ROUTE_NAMES.COMMUNITY,
          meta: { title: 'Community' },
          component: () => import('./components-v2/community/UserCommunity.vue'),
        },
        {
          path: 'upgrade/bulk-checkout-success',
          name: ROUTE_NAMES.UPGRADE_BULK_SUCCESS,
          meta: { title: 'Bulk Checkout Success' },
          component: () => import('./components-v2/my-plan/BulkCheckoutSuccess.vue'),
        },
        {
          path: 'upgrade/checkout-success',
          name: ROUTE_NAMES.UPGRADE_SUCCESS,
          meta: { title: 'Checkout Success' },
          component: () => import('./components-v2/my-plan/CheckoutSuccess.vue'),
        },

        {
          path: 'upgrade/:id',
          name: ROUTE_NAMES.UPGRADE,
          meta: { title: 'Upgrade Plan' },
          component: () => import('./components-v2/my-plan/UpgradePlan.vue'),
          beforeEnter: (to, from, next) => util.landingToPricingRouter(to, from, next),
        },
        {
          path: 'stripe-transition',
          name: ROUTE_NAMES.STRIPE_TRANSITION,
          component: () => import('./components-v2/my-plan/StripeTransition.vue'),
        },
        {
          path: 'direct-bulk-purchase',
          name: ROUTE_NAMES.DIRECT_BULK_PURCHASE,
          component: () => import('./components-v2/my-plan/DirectBulkPurchase.vue'),
        },

        {
          path: 'user-plan',
          name: ROUTE_NAMES.USER_PLAN,
          meta: { title: 'My Plan' },
          component: () => import('./components-v2/my-plan/UserPlan.vue'),
        },
        {
          path: 'report/:id/:audienceId?',
          name: ROUTE_NAMES.PRESENTATION_REPORT,
          meta: { title: 'Presentation Report' },
          component: () => import('./components-v2/presentation-report/ReportPageMultiSessionWrapper.vue'),
        },
        {
          path: 'integrations',
          name: ROUTE_NAMES.INTEGRATIONS,
          meta: { title: 'Integrations' },
          component: () => import('./components-v2/integrations/Integrations.vue'),
          beforeEnter: (to, from, next) => {
            const isPresenterWhiteLabel = util.checkWhiteLabelDomain()
            const hasAccess = store.state.currentUser.identifyProvider !== 'skoletube' && !isPresenterWhiteLabel
            if (!hasAccess) {
              next({ name: ROUTE_NAMES.ERROR_404 })
              return
            }
            next()
          },
        },
        {
          path: 'integrations/:id',
          name: ROUTE_NAMES.INTEGRATIONS_DETAIL,
          meta: { title: 'Integrations Detail' },
          component: () => import('./components-v2/integrations/Integrations.vue'),
        },
        {
          path: 'white-label',
          name: ROUTE_NAMES.WHITE_LABEL,
          meta: { title: 'White Label configuration' },
          component: () => import('./components-v2/white-label/WhiteLabel.vue'),
        },
        {
          path: 'referral',
          name: ROUTE_NAMES.REFERRAL,
          meta: { title: 'Refer a Friend' },
          component: () => import('./components-v2/referral/Referral.vue'),
        },
        {
          path: 'admin-analytics',
          name: ROUTE_NAMES.ADMIN_ANALYTICS,
          meta: { title: 'Admin Analytics' },
          component: () => import('./components-v2/admin-analytics/AdminAnalyticalWrapper.vue'),
        },
        {
          path: 'report',
          name: ROUTE_NAMES.REPORT_SESSION,
          meta: { title: 'My Sessions' },
          component: () => import('./components-v2/report-session/ReportSessionPage.vue'),
        },
      ],
    },
    {
      path: '/demo',
      name: ROUTE_NAMES.DEMO_PRESENTATION,
      component: () => import('./components-v2/presentation-editor/demo-mode/DemoPage.vue'),
    },
    {
      path: '/pages/sso',
      name: ROUTE_NAMES.SSO,
      meta: { title: 'Login with SSO' },
      component: () => {
        const variant = getLocalVariant('UI_VARIANT')
        if (variant === LOCAL_VARIANTS.TREATMENT) {
          return import('./components-v2/auth/SSOV3.vue')
        }
        return import('./components-v2/sso/SSO.vue')
      },
    },
    {
      path: '/auth',
      name: ROUTE_NAMES.AUTH,
      meta: { title: 'Login' },
      component: () => import('./components-v2/common/auth/Auth.vue'),
    },
    {
      path: '/pages',
      redirect: '/pages/register',
    },
    {
      path: '/apps/community',
      name: ROUTE_NAMES.COMMUNITY,
    },
    {
      path: '/pages/login',
      name: ROUTE_NAMES.LOGIN,
      meta: { title: 'Login' },
      component: () => {
        const variant = getLocalVariant('UI_VARIANT')
        if (variant === LOCAL_VARIANTS.TREATMENT) {
          return import('./components-v2/auth/LoginV3.vue')
        }
        return import('./components-v2/login/LoginV2.vue')
      },
    },
    {
      path: '/pages/confirm-third-party-access',
      name: ROUTE_NAMES.CONFIRM_OPENAI,
      meta: { title: 'confirm-third-party-access' },
      component: () => import('@/components-v2/confirm-third-party-access/index.vue'),
    },
    {
      path: '/pages/register',
      name: ROUTE_NAMES.REGISTER,
      meta: { title: 'Create your account' },
      component: () => {
        const variant = getLocalVariant('UI_VARIANT')
        if (variant === LOCAL_VARIANTS.TREATMENT) {
          return import('./components-v2/auth/RegisterV3.vue')
        }
        return import('./components-v2/register/RegisterV2.vue')
      },
    },
    {
      path: '/pages/forgot-password',
      name: ROUTE_NAMES.FORGOT_PASSWORD,
      meta: { title: 'Forgot password' },
      component: () => {
        const variant = getLocalVariant('UI_VARIANT')
        if (variant === LOCAL_VARIANTS.TREATMENT) {
          return import('./components-v2/auth/ForgotPasswordV3.vue')
        }
        return import('./components-v2/forgot-password/ForgotPassword.vue')
      },
    },
    {
      path: '/pages/token',
      name: ROUTE_NAMES.TOKEN,
      component: () => import('./components-v2/token/Token.vue'),
    },
    {
      path: '/pages/error-404',
      name: ROUTE_NAMES.ERROR_404,
      meta: { title: 'Page not found' },
      component: () => import('./components-v2/404/Error404.vue'),
    },
    {
      path: '/pages/clear-token',
      name: ROUTE_NAMES.CLEAR_TOKEN,
      component: () => import('./components-v2/token/ClearToken.vue'),
    },
    {
      path: '*',
      redirect: { name: ROUTE_NAMES.ERROR_404 },
    },
    {
      path: '/presentation/:id',
      name: ROUTE_NAMES.PRESENTATION_DETAIL,
      component: () => import('./components-v2/presentation-editor/PresentationEditorMultiSessionWrapper.vue'),
    },
    {
      path: '/presentation-google-slide/:id',
      name: ROUTE_NAMES.PRESENTATION_DETAIL_GOOGLE_SLIDE,
      component: () => import('./components-v2/presentation-editor/PresentationEditorGoogleSlidePage.vue'),
    },
    {
      path: '/presentation-google-slide-error',
      name: ROUTE_NAMES.PRESENTATION_ERROR_GOOGLE_SLIDE,
      component: () => import('./components-v2/presentation-editor/PresentationErrorGoogleSlidePage.vue'),
    },
    {
      path: '/presentation/:id/backstage',
      name: ROUTE_NAMES.PRESENTATION_BACKSTAGE,
      component: () => import('./components-v2/presentation-editor/PresentationEditorWrapper.vue'),
    },
    {
      path: '/share/:id',
      name: ROUTE_NAMES.PRESENTATION_SHARE,
      component: () => import('./components-v2/presentation-editor/PresentationSharingView.vue'),
      meta: { noindex: true },
    },
    {
      path: '/shared-template/:id',
      name: ROUTE_NAMES.TEMPLATE_VIEW,
      component: () => import('./components-v2/template/TemplateView.vue'),
    },
    {
      path: '/template/:shareCode',
      name: ROUTE_NAMES.TEMPLATE_PREVIEW,
      component: () => import('./components-v2/template/TemplatePreview.vue'),
    },
    {
      path: '/live-demo/',
      name: ROUTE_NAMES.PRESENTATION_PLAY_DEMO,
      component: () => import('./components-v2/presentation-play-demo/PresentationPlayDemo.vue'),
    },
    {
      path: '/save-presentation/',
      name: ROUTE_NAMES.SAVE_PRESENTATION,
      component: () => import('./components-v2/save-presentation/SavePresentation.vue'),
    },
    {
      path: '/google-redirect',
      name: ROUTE_NAMES.GOOGLE_REDIRECT,
      component: () => import('./components-v2/common/auth/GoogleRedirect.vue'),
    },
    {
      path: '/google-auth',
      name: ROUTE_NAMES.GOOGLE_AUTH,
      component: () => import('./components-v2/common/auth/GoogleAuthView.vue'),
    },
    {
      path: '/integration-google-slide',
      name: 'IntegrationGoogleSlide',
      component: () => import('./components-v2/integrations/googleSlide.vue'),
    },
    {
      path: '/integration-google-slide-preseting',
      name: 'IntegrationGoogleSlidePresenting',
      component: () => import('./components-v2/integrations/googleSlidePresenting.vue'),
    },
    {
      path: '/google-drive-shortcut',
      name: ROUTE_NAMES.GOOGLE_DRIVE_SHORTCUT,
      component: () => import('./components-v2/common/auth/GoogleDriveShortcut.vue'),
    },
    {
      path: '/zoho-auth',
      name: ROUTE_NAMES.ZOHO_AUTH,
      component: () => import('./components-v2/zoho-auth/ZohoAuth.vue'),
    },
  ],
}

if (Config.env === 'local') {
  configRouter.base = process.env.BASE_URL
}

if (Config.integrationPlatform === 'GoogleSlide') {
  delete configRouter.base
  configRouter.routes = configRouter.routes.filter((item) => {
    return item.path !== '/' && item.path !== '*'
  })
}

const router = new Router(configRouter)

// eslint-disable-next-line sonarjs/cognitive-complexity
router.beforeEach((to, from, next) => {
  const {
    name, query, params, redirectedFrom, fullPath, path,
  } = to

  // cache from
  store.dispatch('presentationEditor/setPreviousRouteAction', from)
  store.dispatch('movePresentation/updateIsAllPresentationsSelected', false)

  if (to.name === 'Team' || to.name === 'TeamFolderDetail') {
    router.push({ name: 'Presentations' })
  }

  if (typeof to.meta === 'function') {
    const { title } = to.meta()

    util.setDocumentTitle(title)
  }

  if (to.meta && typeof to.meta !== 'function' && to.meta.title) {
    util.setDocumentTitle(to.meta.title)
  }

  if (to.name === 'ZoomApp') {
    next()
  }

  const token = cookie.getValue('ahaToken')
  const url = `${document.location.origin}${fullPath}`

  store.commit('SET_ROUTE', to)
  mixpanelUtils.track(`PV > ${name}`, {
    fullPath,
    ...params,
  })

  if (name === 'Register') {
    util.trackGA4AndMixpanel('register_page_view', {
      fullPath,
      ...params,
      eventLabel: getLocalVariant('UI_VARIANT'),
    })
  }

  if (window.sendinblue) {
    window.sendinblue.page(name, {
      ma_url: url,
      title: name,
      ma_title: name,
      ma_path: fullPath,
    })
  }

  const isEmbed = util.getParamValue('embedded')

  if (
    !isEmbed &&
    NO_CHAT_ROUTES.indexOf(name) < 0 &&
    !path.includes('/ms-teams')
  ) {
    zoho.init()
  }
  const isGuest = cookie.getValue(AHA_GUEST_USER)
  if (token) {
    Vue.http.headers.common.Authorization = `Bearer ${token}`
    store.dispatch('setAuthAction', true)
  }

  if (Config.integrationPlatform === 'GoogleSlide') {
    next()
    return
  }

  if (
    PUBLIC_ROUTES.indexOf(name) === -1
    && !store.state.isAuthenticated
  ) {
    query.redirect = to.fullPath
  }

  if (from.name === 'Presentations' && !cookie.getValue('hideOnboardingTooltip')) {
    cookie.setValue('hideOnboardingTooltip', true)
  }

  if (PUBLIC_ROUTES.indexOf(name) > -1) {
    next()
    return
  }

  // if logged in as normal user, open all routes
  if (token && !isGuest) {
    next()
    return
  }
  if (isGuest) {
    if (OPEN_ROUTES_FOR_GUEST.indexOf(name) > -1) {
      next()
      return
    }
    router.push({ path: '/pages/register', query })
    return
  }

  if (name === 'Upgrade') {
    if (params.id === 'essential-monthly') {
      query.content = 'essential_monthly'
    }

    if (params.id === 'essential-yearly') {
      query.content = 'essential_annually'
    }

    if (params.id === 'pro-monthly') {
      query.content = 'pro_monthly'
    }

    if (params.id === 'pro-yearly') {
      query.content = 'pro_annually'
    }

    if (query.content) {
      query.source = 'pricing'
    }
  }

  // https://ahaslides.atlassian.net/browse/AHA-3127
  // if user without account is invited, redirect to signup
  if (query.edit || query['edit-folder']) {
    try {
      const decryptedQuery = textUtils.decryptText(query.edit) || textUtils.decryptText(query['edit-folder'])
      const inviteCode = util.getParamValue('invite-code', decryptedQuery)
      query['invite-code'] = inviteCode
    } catch (err) {
      Sentry.captureException(err)
    }
  }
  if (
    (redirectedFrom &&
      (redirectedFrom === '/' || redirectedFrom.indexOf('/?') === 0)) ||
    query['invite-code']
  ) {
    router.push({ path: '/pages/register', query })
    return
  }
  if (path.includes('ms-teams')) {
    router.replace({ name: 'MSTeams-TabConfig', query })
  } else {
    router.push({ path: '/pages/login', query })
  }

  next()
})

router.beforeEach((to, _, next) => {
  applySeoForRoute(to)
  next()
})

router.afterEach((to, from) => {
  // wait until the route is loaded before removing inital polling loader
  // to avoid brief blank screen
  store.dispatch('routeChangeAction', {
    to,
    from,
  })
  util.removeInitialLoader()
})

router.onError(() => {
  store.dispatch('setIsServerConnectableAction', false)
})

// Register prefetch handler with the route-prefetch plugin
// This avoids circular dependency while keeping all plugins declared in store.js
const allRoutes = router.options.routes.flatMap((r) => [r, ...(r.children || [])])
const routeMap = allRoutes.reduce((map, route) => {
  if (route.name) {
    map[route.name] = route
  }
  return map
}, {})

registerPrefetchHandler((currentRouteName) => {
  const routeNamesToPrefetch = getRoutesToPrefetch(currentRouteName)

  if (routeNamesToPrefetch.length === 0) {
    return
  }

  const componentImportFns = routeNamesToPrefetch
    .map((routeName) => {
      const route = routeMap[routeName]
      return route?.component && typeof route.component === 'function' ? route.component : null
    })
    .filter(Boolean)

  if (componentImportFns.length > 0) {
    prefetchMultipleComponents(componentImportFns)
  }
})

export default router
